import { HttpBackend, HttpClient } from '@angular/common/http';
import { TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { Observable, map } from 'rxjs';

/**
 * Loads `assets/i18n/<lang>.json`, dropping every key whose value is an empty
 * string.
 *
 * This exists because of a sharp edge in ngx-translate: `getParsedResult()`
 * only falls back to `defaultLang` when the looked-up value is `undefined`. An
 * empty string is a perfectly good value as far as it is concerned, so a key
 * sitting at `""` renders as blank text rather than falling back.
 *
 * The extractor writes `""` for every key a translator has not filled in yet,
 * which is exactly the state a partially translated locale is in — so without
 * this, shipping an incomplete `sv.json` would blank out most of the UI instead
 * of showing English.
 *
 * Removing those keys entirely restores the intended behaviour: untranslated
 * strings fall through to `en.json`, where every value is populated. The JSON
 * files keep their empty entries, which is what the translation workflow (and
 * `npm run i18n`) expects.
 */
export class EmptySkippingTranslateLoader implements TranslateLoader {

  private readonly inner: TranslateHttpLoader;

  constructor(http: HttpClient, prefix = './assets/i18n/', suffix = '.json') {
    this.inner = new TranslateHttpLoader(http, prefix, suffix);
  }

  getTranslation(lang: string): Observable<Record<string, string>> {
    // TranslateHttpLoader is typed as returning `Object`; the payload is a flat
    // string map, which is what the extractor writes.
    return this.inner.getTranslation(lang).pipe(
      map((payload) => {
        const raw = (payload ?? {}) as Record<string, string>;
        const out: Record<string, string> = {};
        for (const key of Object.keys(raw)) {
          const value = raw[key];
          if (typeof value === 'string' && value.trim() === '') { continue; }
          out[key] = value;
        }
        return out;
      })
    );
  }
}

/**
 * Builds the loader used by `TranslateModule.forRoot()`.
 *
 * Deliberately on `HttpBackend` rather than `HttpClient`: that skips the
 * interceptor chain, and `apiAuthInterceptor` is fatal to this particular
 * request. Loading the locale is the *first* step of `CoreProvider.init()`,
 * which itself runs from the CoreProvider constructor — so the request is
 * issued while CoreProvider is still being constructed, and the interceptor's
 * `inject(CoreProvider)` throws `NG0200: Circular dependency`. Both locale
 * files then failed before ever reaching the network, `I18nService.init()`
 * swallowed the error, and every string fell back to its key: the whole UI
 * rendered in English no matter which language was stored. (Even without the DI
 * cycle the interceptor would still call `auth.getLiveTokens()`, which reads a
 * StorageService that `init()` has not opened yet.)
 *
 * The locale files are static assets anyway — no bearer token, no 498 refresh
 * handling, nothing the interceptor has to contribute.
 */
export const createTranslateLoader = (backend: HttpBackend): TranslateLoader =>
  new EmptySkippingTranslateLoader(new HttpClient(backend), './assets/i18n/', '.json');
